/**
 * PSD 编码（导出为 Photoshop 格式，ADR-0014）。写入优先——只保证自己写
 * 出去的 PSD 能被 Photoshop 打开，不做完整的 PSD 读写。
 *
 * 用 `ag-psd`（MIT，纯 JS/TS，无运行时依赖，浏览器可跑）做实际的二进制
 * 编码。这个模块的职责只是把它的类型隔离在 IO 层——应用层用下面这套
 * 精简过的纯数据接口，不直接 import `ag-psd` 的类型，跟 `io/codecs/index.ts`
 * 包 wasm 编解码器细节同一个纪律（第三方格式库的边界收在 IO 层）。
 *
 * 像素走 `imageData`（裸 `{data, width, height}`）不走 `canvas`：一是
 * `ag-psd` 自己的 `ReadOptions.useImageData` 文档就说 canvas 路径有
 * "premultiplied alpha 污染像素"的已知坑；二是这样这个模块不依赖
 * `HTMLCanvasElement`，在 vitest 的 node 环境里也能直接跑单测，不用
 * 引入 canvas polyfill。
 */
import { readPsd, writePsdUint8Array, type Layer as AgPsdLayer, type Psd as AgPsdPsd } from 'ag-psd';

/** 解码结果——直接复用 ag-psd 自己的字段形状，应用层（`app/file/psd-import.ts`，ADR-0015）只读字段，不 import `ag-psd` 本身。 */
export type PsdDecoded = AgPsdPsd;
export type PsdDecodedLayer = AgPsdLayer;

export interface PsdRasterPixels {
  /** RGBA8，直通 alpha（非预乘），行主序——跟 `CompositeResult.pixels` 同一格式。 */
  readonly data: Uint8Array;
  readonly width: number;
  readonly height: number;
}

export interface PsdTextInfo {
  /** 全部文本 run 拼接后的纯文本。 */
  readonly text: string;
  readonly fontFamily?: string;
  readonly fontSize?: number;
  readonly color?: { readonly r: number; readonly g: number; readonly b: number };
  readonly vertical: boolean;
}

export interface PsdLayerInput {
  readonly name: string;
  readonly hidden: boolean;
  /** 0–1。 */
  readonly opacity: number;
  /** 已经映射成 ag-psd 认的字符串（`'color burn'` 这类带空格的 PS 名字）。 */
  readonly blendMode: NonNullable<AgPsdLayer['blendMode']>;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  /** 省略 = 图层组（用 `children` 表达），ag-psd 不允许同时给 `imageData` 和 `children`。 */
  readonly pixels?: PsdRasterPixels;
  readonly text?: PsdTextInfo;
  readonly children?: readonly PsdLayerInput[];
  /** 仅图层组：面板里是否展开。 */
  readonly opened?: boolean;
  /**
   * 图层蒙版/图层样式：直接复用 ag-psd 自己的字段形状（不是拍平重设计），
   * 导出侧（`export-psd.ts`，ADR-0014）目前不写这两项——图层样式选择
   * 烧录进像素，蒙版没有写入需求；这里先加上纯粹是为了让 PSD 导入
   * （ADR-0015）的往返测试能用真实的 ag-psd 字节验证蒙版/效果解析逻辑，
   * 不用另外造一套裸字节。
   */
  readonly mask?: AgPsdLayer['mask'];
  readonly effects?: AgPsdLayer['effects'];
}

export interface PsdDocumentInput {
  readonly width: number;
  readonly height: number;
  readonly layers: readonly PsdLayerInput[];
}

function toAgPsdLayer(input: PsdLayerInput): AgPsdLayer {
  const positioned: AgPsdLayer = {
    name: input.name,
    hidden: input.hidden,
    opacity: input.opacity,
    blendMode: input.blendMode,
    left: input.left,
    top: input.top,
    right: input.right,
    bottom: input.bottom,
  };

  if (input.children !== undefined) {
    return { ...positioned, children: input.children.map(toAgPsdLayer), opened: input.opened ?? true };
  }

  const withPixels: AgPsdLayer =
    input.pixels === undefined
      ? positioned
      : {
          ...positioned,
          imageData: { data: input.pixels.data, width: input.pixels.width, height: input.pixels.height },
        };

  const withMaskAndEffects: AgPsdLayer = {
    ...withPixels,
    ...(input.mask === undefined ? {} : { mask: input.mask }),
    ...(input.effects === undefined ? {} : { effects: input.effects }),
  };

  if (input.text === undefined) return withMaskAndEffects;
  return {
    ...withMaskAndEffects,
    text: {
      text: input.text.text,
      orientation: input.text.vertical ? 'vertical' : 'horizontal',
      style: {
        ...(input.text.fontFamily === undefined ? {} : { font: { name: input.text.fontFamily } }),
        ...(input.text.fontSize === undefined ? {} : { fontSize: input.text.fontSize }),
        ...(input.text.color === undefined ? {} : { fillColor: input.text.color }),
      },
    },
  };
}

/** 编码成 PSD 字节。不是异步操作，返回 Promise 只是跟其它编码器（`ExportEncoder`）签名一致。 */
export async function encodePsdBytes(doc: PsdDocumentInput): Promise<Uint8Array> {
  const psd: AgPsdPsd = {
    width: doc.width,
    height: doc.height,
    // 3 = ColorMode.RGB——ag-psd 把它声明成 `const enum`，isolatedModules 下
    // 不能从依赖里 import 常量枚举本身，直接用文档里写明的数值字面量
    colorMode: 3,
    children: doc.layers.map(toAgPsdLayer),
  };
  return writePsdUint8Array(psd, {});
}

/**
 * 解码 PSD 字节（ADR-0015，导入）。`useImageData: true`——跟写入侧同一个
 * 理由，避免 canvas 路径的 premultiplied alpha 坑，且不需要真实 Canvas，
 * node 环境的单测也能跑。`totalMemoryLimit` 转发给调用方指定（默认沿用
 * ag-psd 自己的 2GB），图层数量/嵌套深度这两项 ag-psd 不做防御，由
 * `app/file/psd-import.ts` 在拿到解码结果之后自己检查。
 */
export async function decodePsdBytes(
  bytes: Uint8Array,
  options: { readonly totalMemoryLimit?: number } = {},
): Promise<PsdDecoded> {
  return readPsd(bytes, {
    useImageData: true,
    ...(options.totalMemoryLimit === undefined ? {} : { totalMemoryLimit: options.totalMemoryLimit }),
  });
}
